import {gql} from "@apollo/client";
import {fetchData} from "../Service/graphQlService";

/**
 * The database counts printed on the four cards of page 07.
 *
 *   INVESTORS    getContacts(type: "Investor").totalCount
 *   PROPERTIES   getProperties.totalCount
 *   AGENTS       getContacts(type: "Agent").totalCount
 *
 * All three resolvers compute their count with a dedicated COUNT query rather than by
 * counting the returned page, so `pageSize: 1` is enough - only the count is selected and
 * no rows are transferred.
 *
 * getProperties' count is `SELECT COUNT(*) FROM PROPERTIES WHERE DELETED IS NULL`, which
 * matches the card's own wording ("mapped commercial assets across U.S. markets") - it is
 * the whole database, not just STRIVE's own listings or closings.
 *
 * The fourth card, "100% investment-properties-focused", is a statement about the firm
 * rather than a measurement, so it is template content - see ProprietaryDatabase.
 */

const ONE_ROW = {page: 1, pageSize: 1};

const GET_CONTACT_COUNT = gql`
  query getBovContactCount($type: String, $pageOptions: PageOptions, $filters: [ContactsFilters]) {
    getContacts(type: $type, pageOptions: $pageOptions, filters: $filters) {
      totalCount
    }
  }
`;

const GET_PROPERTY_COUNT = gql`
  query getBovPropertyCount($pageOptions: PageOptions, $filters: [Filters]) {
    getProperties(pageOptions: $pageOptions, filters: $filters) {
      totalCount
    }
  }
`;

/** Comma-grouped, as the template prints them ("109,562"). */
export function formatCount(value) {
    const n = Number(value);
    if (!isFinite(n) || n <= 0) return '-';
    return Math.round(n).toLocaleString('en-US');
}

async function contactCount(type) {
    const res = await fetchData(GET_CONTACT_COUNT, {type, pageOptions: ONE_ROW, filters: []});
    return res && res.data && res.data.getContacts && res.data.getContacts.totalCount;
}

async function propertyCount() {
    const res = await fetchData(GET_PROPERTY_COUNT, {pageOptions: ONE_ROW, filters: []});
    return res && res.data && res.data.getProperties && res.data.getProperties.totalCount;
}

/**
 * Each count is fetched independently so one failing resolver costs only its own card.
 */
export async function loadBovCounts() {
    const [investors, properties, agents] = await Promise.all([
        contactCount('Investor').catch(() => null),
        propertyCount().catch(() => null),
        contactCount('Agent').catch(() => null),
    ]);

    return {
        investors: formatCount(investors),
        properties: formatCount(properties),
        agents: formatCount(agents),
    };
}
